import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { 
  ArrowLeft, Trophy, Flame, Target, Zap, TrendingUp, TrendingDown,
  Calendar, Award, BarChart3, Crown, Map, Star, Clock
} from 'lucide-react';

const StatsPage = ({ user, onBack }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const sessionToken = localStorage.getItem('session_token');
        const headers = {};
        if (sessionToken) {
          headers['Authorization'] = `Bearer ${sessionToken}`;
        }

        const response = await fetch(`${backendUrl}/api/stats/me`, {
          credentials: 'include',
          headers
        });

        if (!response.ok) {
          throw new Error('Failed to fetch stats');
        }

        const data = await response.json();
        setStats(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading your stats...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Card className="bg-black/50 border-red-500/50 p-8 text-center max-w-md">
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={onBack} variant="outline" className="text-white border-white/20">
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  const { whiching_hour, journey, streaks, rankings, decks, recent_games, score_distribution } = stats;

  // Calculate score distribution for chart
  const maxScore = Math.max(...Object.keys(score_distribution).map(Number), 10);
  const distributionArray = [];
  for (let i = 0; i <= Math.min(maxScore, 15); i++) {
    distributionArray.push({ score: i, count: score_distribution[String(i)] || 0 });
  }
  const maxCount = Math.max(...distributionArray.map(d => d.count), 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button 
            onClick={onBack} 
            variant="ghost" 
            className="text-gray-400 hover:text-white"
            data-testid="stats-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-3">
            {stats.user.picture && (
              <img src={stats.user.picture} alt="" className="w-10 h-10 rounded-full" />
            )}
            <div className="text-right">
              <h1 className="text-xl font-bold text-white">{stats.user.nickname || stats.user.name}</h1>
              <p className="text-gray-400 text-sm">Personal Stats</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'decks', label: 'Decks', icon: Target },
            { id: 'history', label: 'History', icon: Clock }
          ].map(tab => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={activeTab === tab.id 
                ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                : 'bg-white/10 border-white/20 text-gray-300 hover:bg-white/20'}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Global Ranking Banner */}
            {rankings.global_rank && (
              <Card className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border-yellow-500/50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Crown className="w-8 h-8 text-yellow-400" />
                    <div>
                      <p className="text-yellow-400 font-bold text-lg">Global Rank #{rankings.global_rank}</p>
                      <p className="text-gray-400 text-sm">Out of {rankings.total_players} players</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-white">{whiching_hour.total_score}</p>
                    <p className="text-gray-400 text-sm">Total Points</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Main Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Total Games */}
              <Card className="bg-black/40 border-white/10 p-4">
                <div className="flex items-center gap-2 text-blue-400 mb-2">
                  <Target className="w-5 h-5" />
                  <span className="text-sm">Games Played</span>
                </div>
                <p className="text-3xl font-bold text-white">{whiching_hour.total_games}</p>
                <p className="text-gray-500 text-xs mt-1">Which&apos;ing Hour</p>
              </Card>

              {/* Average Score */}
              <Card className="bg-black/40 border-white/10 p-4">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-sm">Avg Score</span>
                </div>
                <p className="text-3xl font-bold text-white">{whiching_hour.avg_score}</p>
                <p className="text-gray-500 text-xs mt-1">Points per game</p>
              </Card>

              {/* Best Score */}
              <Card className="bg-black/40 border-white/10 p-4">
                <div className="flex items-center gap-2 text-yellow-400 mb-2">
                  <Trophy className="w-5 h-5" />
                  <span className="text-sm">Best Score</span>
                </div>
                <p className="text-3xl font-bold text-white">{whiching_hour.best_score}</p>
                <p className="text-gray-500 text-xs mt-1">Personal record</p>
              </Card>

              {/* Best Streak */}
              <Card className="bg-black/40 border-white/10 p-4">
                <div className="flex items-center gap-2 text-orange-400 mb-2">
                  <Flame className="w-5 h-5" />
                  <span className="text-sm">Best Streak</span>
                </div>
                <p className="text-3xl font-bold text-white">{streaks.best_win_streak}</p>
                <p className="text-gray-500 text-xs mt-1">Consecutive correct</p>
              </Card>
            </div>

            {/* Journey Mode Stats */}
            <Card className="bg-black/40 border-white/10 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Map className="w-6 h-6 text-yellow-400" />
                <h2 className="text-xl font-bold text-white">The Journey</h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Total Attempts</p>
                  <p className="text-2xl font-bold text-white">{journey.total_journeys}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Best Run</p>
                  <p className="text-2xl font-bold text-yellow-400">{journey.best_run} decks</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total Decks Passed</p>
                  <p className="text-2xl font-bold text-white">{journey.total_decks_passed}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Completions</p>
                  <p className="text-2xl font-bold text-green-400">{journey.completions}</p>
                </div>
              </div>
            </Card>

            {/* Streaks & Activity */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Streaks */}
              <Card className="bg-black/40 border-white/10 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-6 h-6 text-cyan-400" />
                  <h2 className="text-xl font-bold text-white">Streaks</h2>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Current Daily Streak</span>
                    <span className="text-xl font-bold text-cyan-400">
                      {streaks.current_daily} {streaks.current_daily === 1 ? 'day' : 'days'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Best Daily Streak</span>
                    <span className="text-xl font-bold text-white">
                      {streaks.best_daily} {streaks.best_daily === 1 ? 'day' : 'days'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Best Win Streak</span>
                    <span className="text-xl font-bold text-orange-400">
                      {streaks.best_win_streak} correct
                    </span>
                  </div>
                </div>
              </Card>

              {/* Score Distribution Chart */}
              <Card className="bg-black/40 border-white/10 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-6 h-6 text-purple-400" />
                  <h2 className="text-xl font-bold text-white">Score Distribution</h2>
                </div>
                
                <div className="flex items-end gap-1 h-32">
                  {distributionArray.map((item, index) => (
                    <div 
                      key={index}
                      className="flex-1 flex flex-col items-center"
                    >
                      <div 
                        className="w-full bg-purple-500/80 rounded-t transition-all hover:bg-purple-400"
                        style={{ height: `${(item.count / maxCount) * 100}%`, minHeight: item.count > 0 ? '4px' : '0' }}
                        title={`Score ${item.score}: ${item.count} games`}
                      />
                      <span className="text-gray-500 text-xs mt-1">{item.score}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-500 text-xs text-center mt-2">Score</p>
              </Card>
            </div>

            {/* Best & Worst Decks */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Best Decks */}
              <Card className="bg-black/40 border-white/10 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                  <h2 className="text-xl font-bold text-white">Best Decks</h2>
                </div>
                
                {decks.best.length > 0 ? (
                  <div className="space-y-3">
                    {decks.best.map((deck, index) => (
                      <div key={deck.deck_type} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span>
                          <span className="text-white">{deck.deck_name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-green-400 font-bold">{deck.avg_score}</span>
                          <span className="text-gray-500 text-sm ml-1">avg</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Play more games to see your best decks!</p>
                )}
              </Card>

              {/* Worst Decks */}
              <Card className="bg-black/40 border-white/10 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingDown className="w-6 h-6 text-red-400" />
                  <h2 className="text-xl font-bold text-white">Needs Improvement</h2>
                </div>
                
                {decks.worst.length > 0 ? (
                  <div className="space-y-3">
                    {decks.worst.map((deck, index) => (
                      <div key={deck.deck_type} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">📚</span>
                          <span className="text-white">{deck.deck_name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-red-400 font-bold">{deck.avg_score}</span>
                          <span className="text-gray-500 text-sm ml-1">avg</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Play more games to identify areas for improvement!</p>
                )}
              </Card>
            </div>
          </div>
        )}

        {/* Decks Tab */}
        {activeTab === 'decks' && (
          <div className="space-y-4">
            <Card className="bg-black/40 border-white/10 p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">All Decks Played</h2>
                <span className="text-gray-400">{decks.all.length} / 100 decks</span>
              </div>
              
              <Progress 
                value={(decks.all.length / 100) * 100} 
                className="h-2 mb-4" 
              />

              {decks.all.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-gray-400 text-sm border-b border-white/10">
                        <th className="pb-2 pr-4">Deck</th>
                        <th className="pb-2 pr-4 text-center">Games</th>
                        <th className="pb-2 pr-4 text-center">Avg</th>
                        <th className="pb-2 text-center">Best</th>
                      </tr>
                    </thead>
                    <tbody>
                      {decks.all.map((deck) => (
                        <tr key={deck.deck_type} className="border-b border-white/5">
                          <td className="py-3 pr-4 text-white">{deck.deck_name}</td>
                          <td className="py-3 pr-4 text-center text-gray-400">{deck.games}</td>
                          <td className="py-3 pr-4 text-center">
                            <span className={deck.avg_score >= 7 ? 'text-green-400' : deck.avg_score >= 4 ? 'text-yellow-400' : 'text-red-400'}>
                              {deck.avg_score}
                            </span>
                          </td>
                          <td className="py-3 text-center text-purple-400 font-bold">{deck.best}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No games played yet. Start playing to track your progress!</p>
              )}
            </Card>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <Card className="bg-black/40 border-white/10 p-6">
              <h2 className="text-xl font-bold text-white mb-4">Recent Games</h2>
              
              {recent_games.length > 0 ? (
                <div className="space-y-3">
                  {recent_games.map((game, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                    >
                      <div>
                        <p className="text-white font-medium">{game.deck_name}</p>
                        <p className="text-gray-500 text-sm">
                          {new Date(game.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-white">{game.score}</p>
                        {game.win_streak > 0 && (
                          <p className="text-orange-400 text-sm flex items-center gap-1 justify-end">
                            <Flame className="w-3 h-3" />
                            {game.win_streak} streak
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No games played yet!</p>
              )}
            </Card>

            {/* Recent Journey Attempts */}
            {journey.recent_journeys && journey.recent_journeys.length > 0 && (
              <Card className="bg-black/40 border-white/10 p-6">
                <h2 className="text-xl font-bold text-white mb-4">Recent Journey Attempts</h2>
                
                <div className="space-y-3">
                  {journey.recent_journeys.map((attempt, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                    >
                      <div>
                        <p className="text-white font-medium">
                          {attempt.is_journey_complete ? '🏆 Journey Complete!' : 'Journey Attempt'}
                        </p>
                        <p className="text-gray-500 text-sm">
                          {new Date(attempt.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-yellow-400">{attempt.decks_completed}</p>
                        <p className="text-gray-500 text-sm">decks passed</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsPage;
